import { CircularProgress, useTheme } from "@mui/material";
import { ModeGoodsOut20Px, useToast } from "@qubit/autoparts";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { setCurrentBatchId } from "~/features/autostorePicking/autostorePicking.slice";
import { StartPickingModal } from "~/features/startPickingModal/StartPickingModal";

import { useConfigurePortsWithRetry } from "~/hooks/useConfigurePortsWithRetry";
import { useDisclosure } from "~/hooks/useDisclosure";
import { parentPortIdFoundInWorkstation } from "~/lib/helpers";

import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";

import {
  noBatchesLeftMessage,
  setCartNumberConfirmed,
  setShowTwoBinComponents
} from "~/redux/actions";
import { sendUserTaskSelectedEvent } from "~/redux/actions/userEvents";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectUserCanPick } from "~/redux/selectors/authSelectors";
import { selectShowTwoBinComponents } from "~/redux/selectors/autostoreSelectors";
import {
  selectWorkstationAutostoreGridId,
  selectSelectedPortId,
  selectThisWorkstation,
  selectWorkstationHasPickingRole
} from "~/redux/selectors/workstationsSelectors";
import autostorePickingApi from "~/redux/warehouse/autostorePicking";
import { useStartPickingOnWorkstationMutation } from "~/redux/warehouse/autostorePicking.hooks";
import { batchesApi } from "~/redux/warehouse/batches";

import { AutostorePickingState } from "~/types/api";

import { MainButton } from "./MainButton";

export function PickingButton() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { palette } = useTheme();
  const trackedPageName = useMixpanelPageName();

  const fusionPortScreenEnabled =
    useAppSelector(selectServerConfig).ap_fusionPortScreenEnabled;

  const canUserPick = useAppSelector(selectUserCanPick);

  const isPickingRoleFound = useAppSelector(selectWorkstationHasPickingRole);
  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const selectedAutostoreGridId = useAppSelector(
    selectWorkstationAutostoreGridId
  );
  const showTwoBinComponents = useAppSelector(selectShowTwoBinComponents);
  const sitePortId = useAppSelector(selectSelectedPortId);
  const { warningToast, errorToast } = useToast();
  const {
    open: startPickingModalIsOpen,
    onClose: onCloseStartPickingModal,
    onOpen: onOpenStartPickingModal
  } = useDisclosure();

  const {
    configurePortsWithRetry,
    isConfigurePortsRetrying,
    multiPortRequestDuration
  } = useConfigurePortsWithRetry({
    workstationMode: "Picking",
    retryThreshold: 1000
  });

  const [startPickingLoading, setStartPickingLoading] =
    useState<boolean>(false);

  const [startPickingOnWorkstation] = useStartPickingOnWorkstationMutation();
  const workstationId = siteWorkstation?.id;
  const deviceId = siteWorkstation?.deviceId ?? "";

  const disablePicking = !canUserPick || !isPickingRoleFound;

  const prefetchStatusCount = batchesApi.usePrefetch("getStatusCount", {
    ifOlderThan: 5
  });

  useEffect(() => {
    if (!disablePicking && selectedAutostoreGridId && sitePortId !== null) {
      prefetchStatusCount({
        gridId: selectedAutostoreGridId,
        portId: sitePortId
      });
    }
  }, [
    disablePicking,
    prefetchStatusCount,
    selectedAutostoreGridId,
    sitePortId
  ]);

  const handlePickingButtonClick = () => {
    if (!!selectedAutostoreGridId && !!sitePortId && isPickingRoleFound) {
      onOpenStartPickingModal();
    }
    mixpanelTrack({
      type: "Button Click",
      label: "Picking Button",
      trackedPageName
    });
  };

  const startPicking = async (
    enableMultiPort?: boolean
  ): Promise<AutostorePickingState | null> => {
    if (!selectedAutostoreGridId || !workstationId) return null;

    try {
      const chosenState = await startPickingOnWorkstation({
        gridId: selectedAutostoreGridId,
        workstationId,
        forceClosePort: true,
        enableMultiPort,
        deviceId,
        temperatureZones: []
      }).unwrap();

      if (!chosenState) return null;

      const { batchId } = chosenState.pickingState;
      dispatch(setCurrentBatchId(batchId));
      await dispatch(
        autostorePickingApi.util.upsertQueryData(
          "getBatchPickingState",
          {
            gridId: selectedAutostoreGridId,
            portId: chosenState.portId,
            batchId
          },
          chosenState.pickingState
        )
      );

      return chosenState.pickingState;
    } catch (err) {
      const message = getMessageFromRtkError(err);
      if (
        message.includes(
          "There is nothing left to pick or no appropriate bin found."
        )
      ) {
        warningToast(noBatchesLeftMessage);
      } else {
        errorToast(message);
      }
      return null;
    }
  };

  const handlePickClick = async () => {
    dispatch(setCartNumberConfirmed(false));
    setStartPickingLoading(true);
    try {
      let batchId: string | null = null;

      if (fusionPortScreenEnabled) {
        const isConfigurePortsSuccess = await configurePortsWithRetry();
        if (!isConfigurePortsSuccess) {
          return null;
        }

        const enableMultiPort = parentPortIdFoundInWorkstation(
          siteWorkstation?.ports
        );

        const pickingStateLocal = await startPicking(
          enableMultiPort || undefined
        );
        batchId = pickingStateLocal?.batchId ?? null;

        if (showTwoBinComponents !== enableMultiPort) {
          dispatch(setShowTwoBinComponents(enableMultiPort));
        }
      } else {
        const pickingStateLocal = await startPicking();

        batchId = pickingStateLocal?.batchId ?? null;
      }

      await dispatch(sendUserTaskSelectedEvent("PICKING"));
      onCloseStartPickingModal();

      return batchId;
    } catch {
      return null;
    } finally {
      setStartPickingLoading(false);
    }
  };

  const hasWorkstation = !!siteWorkstation;

  const alert =
    (!hasWorkstation && t("no workstation selected")) ||
    (!isPickingRoleFound && hasWorkstation && t("no picking role found")) ||
    (!canUserPick && t("user has insufficient privileges")) ||
    undefined;

  const alertKind =
    (!hasWorkstation && "workstation") ||
    (!isPickingRoleFound && hasWorkstation && "workstation") ||
    (!canUserPick && "user") ||
    undefined;

  return (
    <>
      <MainButton
        alert={alert}
        alertKind={alertKind}
        disabled={disablePicking}
        onClick={handlePickingButtonClick}
        icon={
          startPickingLoading ? (
            <CircularProgress size={30} color="primary" />
          ) : (
            <ModeGoodsOut20Px
              width="30px"
              height="30px"
              fill={palette.primary.contrastText}
            />
          )
        }
      >
        {t("nav.link.picking")}
      </MainButton>

      <StartPickingModal
        open={startPickingModalIsOpen}
        onClose={onCloseStartPickingModal}
        onHomeButtonClickCallback={onCloseStartPickingModal}
        onNextPickButtonClickCallback={handlePickClick}
        maxWidth="sm"
        title={`${t("start picking")}?`}
        pickButtonText={t("start")}
        isConfigurePortsRetrying={isConfigurePortsRetrying}
        multiPortRequestDuration={multiPortRequestDuration}
      />
    </>
  );
}
